import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Rating,
  Avatar,
  IconButton,
} from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from '../api/axios';

const getFullImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads')) return url;
  if (url.startsWith('uploads')) return '/' + url;
  if (url.startsWith('/')) return url;
  return url;
};

const AUTOPLAY_DELAY = 6000;

// Ширина карточки по брейкпоинтам: ~1 карточка на мобильных, 2 на планшетах, 2.5 на десктопе
const CARD_WIDTH_SX = {
  xs: '82%',
  sm: 'calc(50% - 12px)',
  md: 'calc(40% - 16px)',
};

const ReviewCard = ({ review }) => (
  <Box
    sx={{
      flex: '0 0 auto',
      width: CARD_WIDTH_SX,
      scrollSnapAlign: 'start',
    }}
  >
    <Box
      sx={{
        height: '100%',
        backgroundColor: 'background.paper',
        borderRadius: 3,
        boxShadow: 3,
        p: { xs: 3, md: 4 },
        textAlign: 'center',
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <FormatQuoteIcon sx={{ fontSize: 36, color: 'primary.main', opacity: 0.4, mb: 1 }} />

      <Typography
        variant="body1"
        sx={{
          fontSize: { xs: '0.95rem', md: '1rem' },
          lineHeight: 1.6,
          mb: 2,
          display: '-webkit-box',
          WebkitLineClamp: 6,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {review.text}
      </Typography>

      <Rating value={review.rating || 0} readOnly size="small" sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={review.photo ? getFullImageSrc(review.photo) : undefined}
          sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}
        >
          {!review.photo && review.authorName ? review.authorName[0].toUpperCase() : null}
        </Avatar>
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {review.authorName}
          </Typography>
          {review.authorRole && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {review.authorRole}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  </Box>
);

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get('/reviews/public');
        setReviews(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Ошибка загрузки отзывов:', error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const getStep = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !container.firstChild) return 0;
    const card = container.firstChild;
    const style = window.getComputedStyle(container);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }, []);

  const scrollToIndex = useCallback((newIndex) => {
    const container = scrollRef.current;
    if (!container) return;
    const total = reviews.length;
    if (total === 0) return;
    const clamped = ((newIndex % total) + total) % total;
    const step = getStep();
    container.scrollTo({ left: clamped * step, behavior: 'smooth' });
    setActiveIndex(clamped);
  }, [reviews.length, getStep]);

  const handlePrev = () => scrollToIndex(activeIndex - 1);
  const handleNext = () => scrollToIndex(activeIndex + 1);

  useEffect(() => {
    if (reviews.length <= 1) return undefined;

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % reviews.length;
        const container = scrollRef.current;
        if (container) {
          const step = getStep();
          container.scrollTo({ left: next * step, behavior: 'smooth' });
        }
        return next;
      });
    }, AUTOPLAY_DELAY);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [reviews.length, getStep]);

  const pauseAutoplay = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  if (loading || reviews.length === 0) {
    return null;
  }

  return (
    <Box id="reviews" sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" sx={{ mb: 6, fontWeight: 700, fontSize: { xs: '2rem', md: '2.75rem' } }}>
          Отзывы родителей
        </Typography>

        <Box
          sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          onMouseEnter={pauseAutoplay}
        >
          {reviews.length > 1 && (
            <IconButton
              onClick={handlePrev}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                mr: 1,
                flexShrink: 0,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}

          <Box
            ref={scrollRef}
            sx={{
              display: 'flex',
              gap: 3,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollBehavior: 'smooth',
              py: 1,
              px: 0.5,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {reviews.map((review) => (
              <ReviewCard review={review} key={review._id} />
            ))}
          </Box>

          {reviews.length > 1 && (
            <IconButton
              onClick={handleNext}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                ml: 1,
                flexShrink: 0,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {reviews.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            {reviews.map((review, i) => (
              <Box
                key={review._id || i}
                onClick={() => scrollToIndex(i)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  bgcolor: i === activeIndex ? 'primary.main' : 'grey.300',
                  transition: 'background-color 0.3s',
                }}
              />
            ))}
          </Box>
        )}

        {/* Мобильные стрелки под каруселью */}
        {reviews.length > 1 && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', gap: 2, mt: 2 }}>
            <IconButton onClick={handlePrev} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleNext} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ReviewsSection;
